import { motion } from "framer-motion";
import { Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, LabelList
} from "recharts";
import { OPTION_COLORS } from "./constants";
import { CustomTooltip, Bar3DHorizontal, renderLegend } from "./ChartComponents";
import { SEX_COLORS, getSexChartData, getTotalOptionsVoted, translateSex } from "./dataUtils";

export function SexTab({ results, options, optionColorMap }) {
  const { t } = useTranslation();
  const sexChartData = getSexChartData(results).map(row => ({
    ...row,
    displayName: translateSex(t, row.name)
  }));
  const totalOptionsVoted = getTotalOptionsVoted(results);

  return (
    <div className="space-y-6" data-testid="sex-tab">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <Users className="h-5 w-5 text-slate-500" />
          <h2 className="text-lg font-heading font-semibold text-slate-900">{t('results.distributionBySex')}</h2>
        </div>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={sexChartData} layout="vertical" margin={{ top: 20, right: 80, left: 20, bottom: 5 }} barCategoryGap="25%">
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tick={{ fill: '#374151', fontSize: 12 }} />
              <YAxis dataKey="displayName" type="category" tick={{ fill: '#374151', fontSize: 13, fontWeight: 600 }} width={80} tickLine={false} axisLine={false} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
              <Legend content={renderLegend} />
              {options.map((option, index) => {
                const isLast = index === options.length - 1;
                return (
                  <Bar key={option} dataKey={option} fill={optionColorMap[option] || OPTION_COLORS[index]} stackId="a" shape={<Bar3DHorizontal />}>
                    <LabelList dataKey={option} position="center" fill="#ffffff" fontSize={11} fontWeight="bold" formatter={(value) => value > 0 ? value : ''} />
                    {isLast && (
                      <LabelList dataKey="total" position="right" fill="#374151" fontSize={12} fontWeight="bold" offset={10} formatter={(value) => `Total: ${value}`} />
                    )}
                  </Bar>
                );
              })}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {Object.entries(results?.by_sex || {})
          .sort(([, a], [, b]) => b.total - a.total)
          .map(([sex, data], sexIndex) => (
          <motion.div key={sex} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 * sexIndex }}
            className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: SEX_COLORS[sex] || '#94a3b8' }} />
              <h3 className="text-lg font-heading font-semibold text-slate-900">{translateSex(t, sex)}</h3>
              <span className="text-sm text-slate-500">({data.total} {t('results.votantsLabel')})</span>
            </div>
            <table className="data-table">
              <thead><tr><th>{t('results.optionColumn')}</th><th className="text-right">{t('results.votesColumn')}</th><th className="text-right">%</th></tr></thead>
              <tbody>
                {[...options]
                  .sort((a, b) => (data.options?.[b] || 0) - (data.options?.[a] || 0))
                  .map((option) => {
                  const votes = data.options?.[option] || 0;
                  const percentage = totalOptionsVoted > 0 ? ((votes / totalOptionsVoted) * 100).toFixed(1) : 0;
                  const originalIndex = options.indexOf(option);
                  return (
                    <tr key={option}>
                      <td><div className="flex items-center gap-2"><div className="w-3 h-3 rounded" style={{ backgroundColor: optionColorMap[option] || OPTION_COLORS[originalIndex] }} /><span className="text-sm">{option}</span></div></td>
                      <td className="text-right font-mono text-sm">{votes}</td>
                      <td className="text-right font-mono text-sm">{percentage}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
